import { useState, useRef, useEffect, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";

/**
 * Custom hook for managing snackbar notifications
 * @returns {Object} Snackbar utilities and component
 */
const useSnackbar = () => {
  // Instead of queue + current message, we'll use an array of active messages
  const [activeMessages, setActiveMessages] = useState([]);
  const timersRef = useRef({});
  const duration = 3500; // Fixed duration in ms

  const pushMessage = useCallback((message, severity) => {
    const id = Date.now() + Math.random();
    const newMessage = { text: message, severity, id };

    setActiveMessages(prev => [...prev, newMessage]);

    timersRef.current[id] = setTimeout(() => {
      setActiveMessages(prev => prev.filter(msg => msg.id !== id));
      delete timersRef.current[id];
    }, duration);
  }, []);
  
  // Handle timers for auto-closing messages
  useEffect(() => {
    // Clean up existing timers when component unmounts
    return () => {
      Object.values(timersRef.current).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, []);

  const showSuccess = useCallback((message) => {
    pushMessage(message, "success");
  }, [pushMessage]);

  const showError = useCallback((message) => {
    pushMessage(message, "error");
  }, [pushMessage]);

  const showInfo = useCallback((message) => {
    pushMessage(message, "info");
  }, [pushMessage]);

  const handleClose = useCallback((id) => {
    setActiveMessages(prev => prev.filter(msg => msg.id !== id));
    
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  // SnackbarComponent now renders multiple notifications
  const SnackbarComponent = useCallback(() => {
    return (
      <>
        {activeMessages.map((message, index) => (
          <Snackbar
            key={message.id}
            open={true}
            onClose={() => handleClose(message.id)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            style={{ 
              zIndex: 10000,  // Ensure it's on top of everything
              top: `${index * 80}px`, // Stack notifications vertically with spacing
            }}
            TransitionProps={{
              onExited: () => {}, 
              appear: true,
              timeout: 0
            }}
            autoHideDuration={null}
          >
            <Alert
              onClose={() => handleClose(message.id)}
              severity={message.severity}
              sx={{ 
                width: "100%",
                boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
              }}
            >
              {message.text}
            </Alert>
          </Snackbar>
        ))}
      </>
    );
  }, [activeMessages, handleClose]);

  return {
    showSuccess,
    showError,
    showInfo,
    SnackbarComponent,
  };
};

export default useSnackbar;
